// Central route definitions used by `src/App.js` and the Container
import React from "react";
import SignInPage from "../app/auth/SignInPage";
// Import navigation icons
import { efficiency, configuration } from "../assets/images/index";

// Helper function to create lazy components from the index exports
const createLazyComponent = (exportName) => {
  return React.lazy(async () => {
    const module = await import("../index");
    return { default: module[exportName] };
  });
};

// Lazy load all components from index
const AppConfig = createLazyComponent("AppConfig");
const AppConfigProjects = createLazyComponent("AppConfigProjects");
const AppConfigAccountId = createLazyComponent("AppConfigAccountId");
const AppConfigServers = createLazyComponent("AppConfigServers");
const AppConfigUsers = createLazyComponent("AppConfigUsers");
const ApplicationConfiguration = createLazyComponent("ApplicationConfiguration");
const AuditLog = createLazyComponent("AuditLog");
const SBOMLanding = createLazyComponent("SBOMLanding");
const CISOSBOMLanding = createLazyComponent("CISOSBOMLanding");
const Projects = createLazyComponent("Projects");
const Tools = createLazyComponent("Tools");
const Business = createLazyComponent("Business");
const LicenseConfigpage = createLazyComponent("LicenseConfigpage");
const RoleAllocation = createLazyComponent("RoleAllocation");
const DevicesConfig = createLazyComponent("DevicesConfig");
const User = createLazyComponent("User");
const CloudAccount = createLazyComponent("CloudAccount");
const CloudCostConfigpage = createLazyComponent("CloudCostConfigpage");
const PIIDataConfiguration = createLazyComponent("PIIDataConfiguration");
const PrivilegeConfiguration = createLazyComponent("PrivilegeConfiguration");
const Schedulers = createLazyComponent("Schedulers");
const StatePrivilegeConfiguration = createLazyComponent("StatePrivilegeConfiguration");
const RiskAssessement = createLazyComponent("RiskAssessement");
const UserManagement = createLazyComponent("UserManagement");
const LdapConfig = createLazyComponent("LdapConfig");
const LicenseConfig = createLazyComponent("LicenseConfig");
const LoggingConfig = createLazyComponent("LoggingConfig");
const SmtpConfig = createLazyComponent("SmtpConfig");
const BambooRoles = createLazyComponent("BambooRoles");
const BitBucketRoles = createLazyComponent("BitBucketRoles");
const ConfluenceCloudRole = createLazyComponent("ConfluenceCloudRole");
const JFrogPermission = createLazyComponent("JFrogPermission");
const JenkinRoles = createLazyComponent("JenkinRoles");
const JiraCloudRoles = createLazyComponent("JiraCloudRoles");
const JiraRoles = createLazyComponent("JiraRoles");
const NexusRoles = createLazyComponent("NexusRoles");
const SonarQubeRoles = createLazyComponent("SonarQubeRoles");
const SeveritySLAConfigurationRoles = createLazyComponent("SeveritySLAConfigurationRoles");
const ToolDynamicRouter = createLazyComponent("ToolDynamicRouter");

// Public (authentication) routes — keep these minimal and only UI components
export const authRoutes = [
  {
    key: "signin",
    path: "/auth",
    // App expects `route.component` to be a React element
    component: <SignInPage />,
    // mark as not part of sidebar navigation
    isNotSideBarNavigation: true,
  },
];

// Protected / application routes shown after successful sign-in
export const routes = [];

// Development-only Component Showcase route
if (process.env.NODE_ENV === 'development') {
  const ComponentShowcase = React.lazy(() =>
    import('../ComponentShowcase/index.jsx')
  );
  routes.push({
    key: "component-showcase",
    path: "/component-showcase/*",
    component: <ComponentShowcase />,
    isNotSideBarNavigation: true,
  });
}

routes.push(
  {
    name: "Configuration",
    path: "configuration",
    component: <Tools />,
    key: "6",
    icon: configuration,
    navigationDisable: true,
    childNavs: [
      {
        name: "CMDB",
        text: "CMDB",
        navigationDisable: true,
        customDropContainerClass: "CMDB",
        key: "6.1",
        childNavs: [
          {
            name: "CMDB Overview",
            text: "CMDB Overview",
            path: "configuration/cmdb/CMDBOverview",
            component: <AppConfig />,
            key: "6.1.1",
          },
          {
            name: "Business Info",
            text: "Business Info",
            path: "configuration/cmdb/BusinessInfoConfig",
            component: <Business />,
            key: "6.1.2",
          },
          {
            name: "Role Allocation",
            text: "Role Allocation",
            path: "configuration/cmdb/BusinessInfoConfig/roleAllocation",
            component: <RoleAllocation />,
            key: "6.1.3",
            isNotSideBarNavigation: true,
          },
          {
            name: "Users",
            text: "Users",
            path: "configuration/cmdb/UserInfoConfig",
            component: <User />,
            key: "6.1.4",
          },
          {
            name: "Devices",
            text: "Devices",
            path: "configuration/cmdb/Devices",
            component: <DevicesConfig />,
            key: "6.1.5",
          },
          {
            name: "Projects",
            text: "Projects",
            path: "configuration/cmdb/projects",
            component: <Projects />,
            key: "6.1.6",
          },
          {
            name: "Cloud Accounts",
            text: "Cloud Accounts",
            path: "configuration/cmdb/cloudaccounttable/cloudaccount",
            component: <CloudAccount />,
            key: "6.1.7",
          },
          {
            name: "AppConfig",
            path: "configuration/cmdb",
            isNotSideBarNavigation: true,
            component: <AppConfig />,
            key: "6.1.8",
          },
          {
            key: "6.1.9",
            name: "Project",
            path: "/configuration/cmdb/:appname/projects",
            isNotSideBarNavigation: true,
            component: <AppConfigProjects />,
          },
          {
            key: "6.1.10",
            name: "Server",
            path: "configuration/cmdb/:appName/Device",
            isNotSideBarNavigation: true,
            component: <AppConfigServers />,
          },
          {
            key: "6.1.11",
            name: "Users",
            path: "configuration/cmdb/:appName/users",
            isNotSideBarNavigation: true,
            component: <AppConfigUsers />,
          },
          {
            key: "6.1.12",
            name: "Account IDs",
            path: "configuration/cmdb/:appName/CloudAccountIDs",
            isNotSideBarNavigation: true,
            component: <AppConfigAccountId />,
          },
          {
            key: "6.1.13",
            name: "Application Configuration",
            text: "Application Configuration",
            path: "configuration/cmdb/applicationConfiguration",
            isNotSideBarNavigation: true,
            component: <ApplicationConfiguration />,
          },
        ],
      },
      {
        name: "Administration",
        text: "Administration",
        path: "configuration/administration/ldapConfig",
        navigationDisable: true,
        customDropContainerClass: "admin",
        component: <LdapConfig />,
        key: "6.2",
        childNavs: [
          {
            name: "UserManagement",
            text: "User Management",
            path: "configuration/administration/userManagement",
            component: <UserManagement />,
            key: "6.2.1",
          },
          {
            name: "CloudCost",
            text: " Cloud Cost",
            path: "configuration/administration/CloudCost",
            component: <CloudCostConfigpage />,
            key: "6.2.2",
          },
          {
            name: "Schedulers",
            text: "Schedulers",
            path: "configuration/administration/Schedulers",
            component: <Schedulers />,
            key: "6.2.3",
          },
          {
            name: "Tools",
            text: "Tools",
            path: "configuration/administration/tools",
            component: <Tools />,
            key: "6.2.4",
          },
          {
            name: "PII Data Configuration",
            text: "PII Data Configuration",
            path: "configuration/administration/PIIDataConfiguration",
            component: <PIIDataConfiguration />,
            key: "6.2.5",
          },
          {
            name: "IAM Policy Categorization",
            text: "IAM Policy Categorization",
            path: "configuration/administration/IAMPolicyCategorization",
            component: <PrivilegeConfiguration />,
            key: "6.2.6",
          },
          {
            name: "AWS State Categorization",
            text: "AWS State Categorization",
            path: "configuration/administration/AWSStateCategorization",
            component: <StatePrivilegeConfiguration />,
            key: "6.2.7",
          },
          {
            name: "RiskAssessement",
            text: "Assessment Questionnaire",
            path: "configuration/administration/AssessmentQuestionnaire",
            component: <RiskAssessement />,
            key: "6.2.8",
          },
        ],
      },
      {
        name: "Settings",
        text: "Settings",
        path: "configuration/settings/tools",
        navigationDisable: true,
        customDropContainerClass: "settings",
        component: <Tools />,
        key: "6.3",
        childNavs: [
          {
            name: "LDAP Configuration",
            text: "LDAP Configuration",
            path: "configuration/settings/ldapConfig",
            component: <LdapConfig />,
            key: "6.3.1",
          },
          {
            name: "SmtpConfig",
            text: "SMTP Configuration",
            path: "configuration/settings/smtpConfig",
            component: <SmtpConfig />,
            key: "6.3.2",
          },
          {
            name: "LicenseConfig",
            text: "License Configuration",
            path: "configuration/settings/licenseConfig",
            component: <LicenseConfig />,
            key: "6.3.3",
          },
          {
            name: "LoggingConfig",
            text: "Logging Configuration",
            path: "configuration/settings/loggingConfig",
            component: <LoggingConfig />,
            key: "6.3.4",
          },
        ],
      },
      {
        name: "AuditLog",
        text: "Audit Log",
        path: "configuration/auditLog",
        component: <AuditLog />,
        key: "6.4",
      },
      // Tool-specific routes
      {
        name: "SonarQube Roles",
        path: "configuration/administration/tools/sonarqubeRole",
        isNotSideBarNavigation: true,
        component: <SonarQubeRoles />,
        key: "6.5",
      },
      {
        name: "Bamboo Roles",
        path: "configuration/administration/tools/bambooRole",
        isNotSideBarNavigation: true,
        component: <BambooRoles />,
        key: "6.6",
      },
      {
        name: "Bitbucket Roles",
        path: "configuration/administration/tools/bitbucketRole",
        isNotSideBarNavigation: true,
        component: <BitBucketRoles />,
        key: "6.7",
      },
      {
        name: "Jenkins Roles",
        path: "configuration/administration/tools/jenkinsRole",
        isNotSideBarNavigation: true,
        component: <JenkinRoles />,
        key: "6.8",
      },
      {
        name: "Nexus Roles",
        path: "configuration/administration/tools/nexusRole",
        isNotSideBarNavigation: true,
        component: <NexusRoles />,
        key: "6.9",
      },
      {
        name: "JFrog Permission",
        path: "configuration/administration/tools/jfrogRole",
        isNotSideBarNavigation: true,
        component: <JFrogPermission />,
        key: "6.10",
      },
      {
        name: "Jira Configuration",
        path: "configuration/administration/tools/jiraconfiguration",
        isNotSideBarNavigation: true,
        component: <JiraRoles />,
        key: "6.11",
      },
      {
        name: "Jira Cloud Roles",
        path: "configuration/administration/tools/jiracloudRole",
        isNotSideBarNavigation: true,
        component: <JiraCloudRoles />,
        key: "6.12",
      },
      {
        name: "Confluence Cloud Roles",
        path: "configuration/administration/tools/confluencecloudRole",
        isNotSideBarNavigation: true,
        component: <ConfluenceCloudRole />,
        key: "6.13",
      },
      {
        key: "6.14",
        name: "License Config",
        path: "configuration/cmdb/Devices/licenseconfigpage",
        isNotSideBarNavigation: true,
        component: <LicenseConfigpage />,
      },
      {
        name: "gnc",
        text: "License Management",
        isNotSideBarNavigation: true,
        path: "/gnc/licenseManagement/licenseconfigpage",
        component: <LicenseConfigpage />,
        key: "6.15",
      },
      {
        key: "6.16",
        name: "Severity SLA Configuration",
        path: "configuration/administration/tools/SeveritySLAConfigurationRole",
        isNotSideBarNavigation: true,
        component: <SeveritySLAConfigurationRoles />,
      },
      // Dynamic route for any tool name
      {
        key: "6.dynamic",
        name: "Tool Dynamic Route",
        path: "configuration/administration/tools/:toolName",
        isNotSideBarNavigation: true,
        component: <ToolDynamicRouter />,
      },
    ],
  },
  // SBOM routes
  {
    name: "SBOM",
    path: "sbom",
    component: <SBOMLanding />,
    key: "sbom",
    isNotSideBarNavigation: false,
  },
  {
    name: "SBOM CISO",
    path: "sbom/ciso",
    component: <CISOSBOMLanding />,
    key: "sbomCiso",
    isNotSideBarNavigation: false,
  }
);

export default { authRoutes, routes };
